import { getCollection, getDocument, setDocument } from './firestore.service'

const BUSINESS_RULES_COLLECTION = 'businessRules'
const BUSINESS_RULES_DOC_ID = 'main'

export const getBusinessRules = async () => {
  try {
    const rules = await getDocument(BUSINESS_RULES_COLLECTION, BUSINESS_RULES_DOC_ID)
    if (!rules) {
      const legacy = await getCollection(
        BUSINESS_RULES_COLLECTION,
        [{ field: 'id', operator: '==', value: BUSINESS_RULES_DOC_ID }],
        null,
        'asc',
        1
      )
      if (legacy && legacy.length) {
        await setDocument(BUSINESS_RULES_COLLECTION, BUSINESS_RULES_DOC_ID, legacy[0], { merge: true })
        return normalizeBusinessRules(legacy[0])
      }
      return getDefaultBusinessRules()
    }
    return normalizeBusinessRules(rules)
  } catch (error) {
    console.error('Error obteniendo reglas de negocio:', error)
    return getDefaultBusinessRules()
  }
}

export const saveBusinessRules = async (rules) => {
  try {
    const rulesData = normalizeBusinessRules(rules)
    const payload = {
      ...rulesData,
      updatedAt: new Date()
    }

    await setDocument(BUSINESS_RULES_COLLECTION, BUSINESS_RULES_DOC_ID, payload, { merge: true })
  } catch (error) {
    console.error('Error guardando reglas de negocio:', error)
    throw error
  }
}

export const getDefaultBusinessRules = () => ({
  outOfStockPolicy: {
    visibility: 'show',
    purchaseBehavior: 'block_purchase',
    inquiryChannel: 'whatsapp',
    labelText: 'Sin stock'
  },
  productIdentification: {
    skuAuto: {
      enabled: false,
      separator: '-',
      parts: [
        { field: 'name', take: 3 },
        { field: 'brand', take: 3 },
        { field: 'counter', take: 4 }
      ]
    },
    serialAuto: {
      enabled: false,
      digits: 15
    }
  },
  ordersSalesMode: {
    mode: 'linked_dependent'
  },
  paymentMethods: {
    enabled: true,
    methods: [
      {
        id: 'cash',
        type: 'cash',
        label: 'Efectivo',
        enabled: true,
        customerNote: '',
        priceAdjustment: null,
        config: {}
      },
      {
        id: 'bank_transfer',
        type: 'bank_transfer',
        label: 'Transferencia',
        enabled: true,
        customerNote: '',
        priceAdjustment: null,
        config: { accounts: [] }
      }
    ]
  }
})

const normalizePriceAdjustment = (value) => {
  if (!value) return null
  const kind = value.kind === 'fixed' ? 'fixed' : 'percent'
  const sign = value.sign === 'surcharge' ? 'surcharge' : 'discount'
  const raw = Number(value.value)
  const parsedValue = Number.isFinite(raw) ? raw : 0
  if (parsedValue === 0) return null
  return { kind, sign, value: parsedValue }
}

const normalizePostnetPrograms = (programs) => {
  if (!Array.isArray(programs)) return []
  return programs
    .filter(Boolean)
    .map((p) => {
      const brand = typeof p?.brand === 'string' ? p.brand : ''
      const logoUrl = typeof p?.logoUrl === 'string' ? p.logoUrl : ''
      const installments = Array.isArray(p?.installments) ? p.installments : []
      const normalizedInstallments = installments
        .filter(Boolean)
        .map((it) => {
          const countRaw = Number(it?.count)
          const count = Number.isFinite(countRaw) && countRaw >= 1 ? Math.round(countRaw) : 1
          const interestRaw = Number(it?.interestPercent)
          const interestPercent = Number.isFinite(interestRaw) ? interestRaw : 0
          const note = typeof it?.note === 'string' ? it.note : ''
          return { count, interestPercent, note }
        })
      return { brand, logoUrl, installments: normalizedInstallments.length ? normalizedInstallments : [{ count: 1, interestPercent: 0, note: '' }] }
    })
}

const normalizeMethod = (method, fallback) => {
  const id = typeof method?.id === 'string' && method.id.trim() ? method.id.trim() : fallback.id
  const type = typeof method?.type === 'string' && method.type.trim() ? method.type.trim() : fallback.type
  const label = typeof method?.label === 'string' && method.label.trim() ? method.label.trim() : fallback.label
  const enabled = method?.enabled !== false
  const customerNote = typeof method?.customerNote === 'string' ? method.customerNote : ''
  const priceAdjustment = type === 'postnet' ? null : normalizePriceAdjustment(method?.priceAdjustment)

  const base = {
    id,
    type,
    label,
    enabled,
    customerNote,
    priceAdjustment
  }

  if (type === 'bank_transfer') {
    const accounts = Array.isArray(method?.config?.accounts) ? method.config.accounts : []
    return { ...base, config: { accounts } }
  }

  if (type === 'postnet') {
    const programs = normalizePostnetPrograms(method?.config?.programs)
    return { ...base, config: { programs } }
  }

  if (type === 'gateway_mercadopago') {
    const publicKey = typeof method?.config?.publicKey === 'string' ? method.config.publicKey : ''
    const accessTokenRef = typeof method?.config?.accessTokenRef === 'string' ? method.config.accessTokenRef : ''
    return { ...base, config: { publicKey, accessTokenRef } }
  }

  return { ...base, config: method?.config && typeof method.config === 'object' ? method.config : {} }
}

export const normalizeBusinessRules = (rules) => {
  const defaults = getDefaultBusinessRules()

  const visibility = rules?.outOfStockPolicy?.visibility === 'hide' ? 'hide' : 'show'
  const allowedPurchaseBehaviors = new Set(['allow_purchase', 'block_purchase', 'inquiry_only'])
  const purchaseBehavior = allowedPurchaseBehaviors.has(rules?.outOfStockPolicy?.purchaseBehavior)
    ? rules.outOfStockPolicy.purchaseBehavior
    : defaults.outOfStockPolicy.purchaseBehavior
  const inquiryChannel = rules?.outOfStockPolicy?.inquiryChannel === 'help_page' ? 'help_page' : 'whatsapp'
  const labelText = typeof rules?.outOfStockPolicy?.labelText === 'string' && rules.outOfStockPolicy.labelText.trim()
    ? rules.outOfStockPolicy.labelText
    : defaults.outOfStockPolicy.labelText

  const skuEnabled = rules?.productIdentification?.skuAuto?.enabled === true
  const separator = typeof rules?.productIdentification?.skuAuto?.separator === 'string'
    ? rules.productIdentification.skuAuto.separator
    : defaults.productIdentification.skuAuto.separator
  const safeSeparator = separator.length > 5 ? separator.slice(0, 5) : separator
  const allowedSkuFields = new Set(['name', 'brand', 'collection', 'color', 'size', 'counter'])
  const rawParts = Array.isArray(rules?.productIdentification?.skuAuto?.parts)
    ? rules.productIdentification.skuAuto.parts
    : defaults.productIdentification.skuAuto.parts
  const parts = rawParts
    .filter(Boolean)
    .map((p) => {
      const field = allowedSkuFields.has(p?.field) ? p.field : 'name'
      const takeRaw = Number(p?.take)
      const take = Number.isFinite(takeRaw) ? Math.max(1, Math.min(30, Math.round(takeRaw))) : 3
      return { field, take }
    })
    .slice(0, 10)
  const normalizedParts = parts.length ? parts : defaults.productIdentification.skuAuto.parts

  const serialEnabled = rules?.productIdentification?.serialAuto?.enabled === true
  const digitsRaw = Number(rules?.productIdentification?.serialAuto?.digits)
  const digits = Number.isFinite(digitsRaw)
    ? Math.max(4, Math.min(30, Math.round(digitsRaw)))
    : defaults.productIdentification.serialAuto.digits

  const allowedModes = new Set(['sales_only', 'linked_independent', 'linked_dependent'])
  const mode = allowedModes.has(rules?.ordersSalesMode?.mode) ? rules.ordersSalesMode.mode : defaults.ordersSalesMode.mode

  const paymentEnabled = rules?.paymentMethods?.enabled !== false
  const rawMethods = Array.isArray(rules?.paymentMethods?.methods) ? rules.paymentMethods.methods : defaults.paymentMethods.methods

  const fallbackByType = new Map(defaults.paymentMethods.methods.map(m => [m.type, m]))
  const methods = rawMethods
    .filter(Boolean)
    .map((m) => normalizeMethod(m, fallbackByType.get(m?.type) || { id: cryptoFallbackId(m), type: m?.type || 'custom', label: m?.label || 'Medio de pago', enabled: true, customerNote: '', priceAdjustment: null, config: {} }))

  return {
    outOfStockPolicy: { visibility, purchaseBehavior, inquiryChannel, labelText },
    productIdentification: {
      skuAuto: { enabled: skuEnabled, separator: safeSeparator, parts: normalizedParts },
      serialAuto: { enabled: serialEnabled, digits }
    },
    ordersSalesMode: { mode },
    paymentMethods: { enabled: paymentEnabled, methods }
  }
}

const cryptoFallbackId = (m) => {
  const base = typeof m?.id === 'string' && m.id.trim() ? m.id.trim() : `${m?.type || 'method'}`
  const suffix = Math.random().toString(16).slice(2, 10)
  return `${base}_${suffix}`
}

export default {
  getBusinessRules,
  saveBusinessRules,
  getDefaultBusinessRules,
  normalizeBusinessRules
}
